import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Package,
  Ruler,
  Weight,
  Loader2,
  X,
  Save,
  AlertCircle,
  Info,
  Sparkles,
  CheckCircle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { PackageDimensions } from '../types';
import { 
  createPackageDimensions, 
  updatePackageDimensions, 
  validatePackageDimensions,
  calculateVolume,
  calculateDimensionalWeight
} from '../services/packageDimensionsService';

interface PackageDimensionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  dimensions?: PackageDimensions | null;
  mode: 'add' | 'edit';
}

export function PackageDimensionsModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  dimensions, 
  mode 
}: PackageDimensionsModalProps) {
  const [formData, setFormData] = useState<Partial<PackageDimensions>>({
    name: '',
    length: 0,
    width: 0,
    height: 0,
    weight: 2.0,
    description: '',
    isDefault: false,
  });
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or dimensions change
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && dimensions) {
        setFormData({
          name: dimensions.name || '',
          length: dimensions.length || 0,
          width: dimensions.width || 0,
          height: dimensions.height || 0,
          weight: dimensions.weight || 2.0,
          description: dimensions.description || '',
          isDefault: dimensions.isDefault || false,
        });
      } else {
        setFormData({
          name: '',
          length: 0,
          width: 0,
          height: 0,
          weight: 2.0,
          description: '',
          isDefault: false,
        });
      }
      setErrors([]);
    }
  }, [isOpen, dimensions, mode]);

  const handleInputChange = (field: keyof PackageDimensions, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    const validationErrors = validatePackageDimensions(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (mode === 'edit' && dimensions?.id) {
        await updatePackageDimensions(dimensions.id, formData);
        toast.success('Package dimensions updated successfully');
      } else {
        await createPackageDimensions(formData as Omit<PackageDimensions, 'id' | 'createdAt' | 'updatedAt'>);
        toast.success('Package dimensions created successfully');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving package dimensions:', error);
      toast.error('Failed to save package dimensions');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate derived values for display
  const volume = formData.length && formData.width && formData.height 
    ? calculateVolume(formData as PackageDimensions)
    : 0;
  
  const dimensionalWeight = formData.length && formData.width && formData.height 
    ? calculateDimensionalWeight(formData as PackageDimensions)
    : 0;

  const suggestedPackages = [
    { name: 'Small Box', length: 8, width: 6, height: 4, weight: 3, description: 'Perfect for spices and small items' },
    { name: 'Medium Box', length: 12, width: 9, height: 6, weight: 5, description: 'Good for medium care packages' },
    { name: 'Large Box', length: 16, width: 12, height: 8, weight: 8, description: 'For large care packages' },
    { name: 'Padded Envelope', length: 12, width: 9, height: 1, weight: 1, description: 'For documents and cards' },
    { name: 'Flat Rate Box (USPS Medium)', length: 11, width: 8.5, height: 5.5, weight: 4, description: 'USPS Priority Mail' },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ 
            type: "spring",
            duration: 0.5,
            bounce: 0.3 
          }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Enhanced Header */}
          <div className="relative bg-gradient-to-r from-purple-500 to-violet-500 p-6 flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-transparent"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                  className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                >
                  <Package className="h-6 w-6 text-white" />
                </motion.div>
                <div>
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-2xl font-bold text-white"
                  >
                    {mode === 'edit' ? 'Edit Package Dimensions' : 'Add Package Dimensions'}
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm"
                  >
                    Configure package sizes for accurate shipping calculations
                  </motion.p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Enhanced Form */}
          <div className="p-8 overflow-y-auto flex-1 bg-gradient-to-b from-gray-50/50 to-white">
            <form id="package-dimensions-form" onSubmit={handleSubmit} className="space-y-8">
              {/* Error Display */}
              <AnimatePresence>
                {errors.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 border border-red-200 rounded-xl p-4"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-red-800 mb-1">Please fix the following errors:</h4>
                        <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                          {errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Quick Templates */}
              {mode === 'add' && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-100"
                >
                  <h4 className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    Quick Templates
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {suggestedPackages.map((pkg) => (
                      <button
                        key={pkg.name}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            name: pkg.name,
                            length: pkg.length,
                            width: pkg.width,
                            height: pkg.height,
                            description: pkg.description,
                          }));
                        }}
                        className="text-left p-4 bg-white border border-purple-200 rounded-xl text-sm hover:bg-purple-50 hover:border-purple-300 transition-all duration-200 hover:shadow-md"
                      >
                        <div className="font-semibold text-purple-800">{pkg.name}</div>
                        <div className="text-purple-600 text-xs font-mono mt-1">
                          {pkg.length}" × {pkg.width}" × {pkg.height}"
                        </div>
                        <div className="text-gray-600 text-xs mt-2">{pkg.description}</div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Basic Information */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Package className="h-5 w-5 text-purple-500" />
                  Package Information
                </h3>
                
                <div className="space-y-6">
                  <div className="space-y-3">
                    <Label htmlFor="name" className="text-base font-semibold text-gray-900">Package Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="e.g., Small Box, Large Envelope"
                      className="h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="description" className="text-base font-semibold text-gray-900">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Optional description of when to use this package size"
                      rows={3}
                      className="rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Dimensions */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Ruler className="h-5 w-5 text-purple-500" />
                  Dimensions (inches)
                </h3>
                
                <div className="grid grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="length" className="text-base font-semibold text-gray-900">Length *</Label>
                    <Input
                      id="length"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.length || ''}
                      onChange={(e) => handleInputChange('length', parseFloat(e.target.value) || 0)}
                      placeholder="12.0"
                      className="h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="width" className="text-base font-semibold text-gray-900">Width *</Label>
                    <Input
                      id="width"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.width || ''}
                      onChange={(e) => handleInputChange('width', parseFloat(e.target.value) || 0)}
                      placeholder="9.0"
                      className="h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="height" className="text-base font-semibold text-gray-900">Height *</Label>
                    <Input
                      id="height"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.height || ''}
                      onChange={(e) => handleInputChange('height', parseFloat(e.target.value) || 0)}
                      placeholder="6.0"
                      className="h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Calculated Values */}
                <AnimatePresence>
                  {volume > 0 && (
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-100"
                    >
                      <h4 className="text-lg font-semibold text-purple-800 mb-4 flex items-center gap-2">
                        <Info className="h-5 w-5" />
                        Calculated Values
                      </h4>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <div className="text-sm text-purple-600 font-medium">Volume</div>
                          <div className="text-2xl font-bold text-purple-800">{volume.toFixed(1)}</div>
                          <div className="text-xs text-purple-500">cubic inches</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <div className="text-sm text-purple-600 font-medium">Dimensional Weight</div>
                          <div className="text-2xl font-bold text-purple-800">{dimensionalWeight}</div>
                          <div className="text-xs text-purple-500">lbs</div>
                        </div>
                      </div>
                      <p className="text-xs text-purple-600 mt-4">
                        Dimensional weight is calculated using the standard divisor of 139 for domestic shipments.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Package Weight */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-6"
              >
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Weight className="h-5 w-5 text-purple-500" />
                  Package Weight <span className="text-red-500">*</span>
                </h3>
                
                <div className="space-y-3">
                  <Label htmlFor="weight" className="text-base font-semibold text-gray-900">Empty Package Weight (ounces) <span className="text-red-500">*</span></Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={formData.weight || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === null) {
                        handleInputChange('weight', undefined);
                      } else {
                        const numValue = parseFloat(value);
                        handleInputChange('weight', isNaN(numValue) ? undefined : numValue);
                      }
                    }}
                    placeholder="2.0"
                    required
                    className="h-12 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                  />
                  <p className="text-sm text-gray-600">
                    Weight of the empty package/box itself. This will be added to the product weights for accurate shipping calculations.
                    <span className="text-red-500 font-medium"> This field is required for proper shipping rate calculations.</span>
                  </p>
                </div>
              </motion.div>

              {/* Default Setting */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-100"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-purple-500/20 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <label className="text-base font-semibold text-gray-900">Set as Default</label>
                      <p className="text-sm text-gray-600 mt-1">
                        Use these dimensions as the default when generating labels
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.isDefault}
                    onCheckedChange={(checked) => handleInputChange('isDefault', checked)}
                  />
                </div>
              </motion.div>
            </form>
          </div>

          {/* Enhanced Footer */}
          <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center justify-end gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-6 h-11 rounded-xl font-medium"
                >
                  Cancel
                </Button>
              </motion.div>
              
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="submit"
                  form="package-dimensions-form"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-purple-500/25"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {mode === 'edit' ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {mode === 'edit' ? 'Update Dimensions' : 'Create Dimensions'}
                    </>
                  )}
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}